/**
 * One particle system drawn into a layer: the smoke, the explosions or the
 * shadows. Particles live in an array kept dense by swap-remove and are walked
 * in spawn order through a doubly linked list of indices, as legacy did, so a
 * death in the middle costs nothing and the draw order stays the spawn order.
 * Each particle is a quad in a Pixi ParticleContainer, one per blend mode the
 * system uses, drawn in the order given: a quad is not a scene-graph child, so
 * a birth or death leaves every other sprite's batching alone, and a group's
 * quads go up as one vertex buffer a frame. Every particle texture sits on the
 * one sprite atlas, which is what lets a group bind a single texture source.
 * Particles and their quads are pooled. Each kind has its own step below; the
 * shadow system draws every particle at its shadow position and size.
 * Fragment emitters are the one emitter kind: a piece of debris flying from an
 * explosion that leaves a smoke puff behind it each frame for fifty frames.
 */
import type { BLEND_MODES, Container, TextureSource } from 'pixi.js';
import { Particle as PixiParticle, ParticleContainer as PixiParticleContainer } from 'pixi.js';
import { clock } from '../Clock';
import { DEVELOPMENT, SHADOW_SCALING } from '../Constants';
import { shadowCoords as graphicsShadowCoords } from '../Graphics';
import { getTexture } from '../Textures';
import {
  clamp as toolsClamp,
  colorLerp as toolsColorLerp,
  easing as toolsEasing,
  rand as toolsRand,
  randCircle as toolsRandCircle,
  randItem as toolsRandItem,
} from '../Tools';
import type Vector from '../Vector';
import type { LoadedTextureName } from '../types/Textures';
import { Particle, rgbToBgr } from './Particle';
import { type ParticleTypeId, particleTypeIdByName, type SmokePair, smokePairs } from './ParticleTypes';

/** A piece of debris in flight, puffing smoke. */
interface Emitter {
  readonly pos: Vector;
  readonly speed: Vector;
  /** 0 to 1; the emitter stops past 1. */
  life: number;
  /** Where its puffs' shadows go. */
  readonly shadows: ParticleContainer;
}

/** How a system draws: its shadow role and the blend modes its spawners pass. */
export interface ParticleContainerOptions {
  /** Draw every particle at its shadow position and size. */
  shadow?: boolean;
  /** The blend modes the spawners pass, one Pixi container each, drawn in this order; normal alone by default. */
  blendModes?: readonly [BLEND_MODES, ...BLEND_MODES[]];
}

/** A frame this long in 60 fps frames is a tab back from the background: nothing is spawned for it. */
const SPAWN_SKIP_FACTOR = 20;

/** A texture on the sprite atlas, any one: a group binds its source and every particle texture must share it. */
const ATLAS_PROBE: LoadedTextureName = 'smoke_1';

/** The quad attributes rewritten every frame; the texture coordinates go up again only when the set of quads changes. */
const DYNAMIC_PROPERTIES = { position: true, rotation: true, vertex: true, color: true, uvs: false } as const;

export class ParticleContainer {
  private readonly maxParticles: number;
  /** The Pixi container for each blend mode a spawner may pass. */
  private readonly groups: ReadonlyMap<BLEND_MODES, PixiParticleContainer>;
  /** The groups in draw order, for the per-frame walk. */
  private readonly groupList: readonly PixiParticleContainer[];
  /** The group a blend mode without one falls back to: the first. */
  private readonly defaultGroup: PixiParticleContainer;
  /** The atlas source every particle texture has to be on. */
  private readonly source: TextureSource;
  private particles: Particle[] = [];
  /** Dead particles with their quads, ready for reuse. */
  private readonly pool: Particle[] = [];
  /** The first particle in spawn order, -1 when empty. */
  private first = -1;
  /** The last particle in spawn order, -1 when empty. */
  private last = -1;
  /** Set by a birth or a death: the groups' quad lists changed, so their texture coordinates go up again. */
  private dirty = false;
  private readonly emitters = new Map<number, Emitter>();
  private emitterId = 0;
  /** Draws every particle at its shadow position and size. */
  private readonly shadow: boolean;

  constructor(
    layer: Container,
    maxParticles: number,
    { shadow = false, blendModes = ['normal'] }: ParticleContainerOptions = {},
  ) {
    this.maxParticles = maxParticles;
    this.shadow = shadow;
    // Any atlas texture hands a group the source it binds
    const atlas = getTexture(ATLAS_PROBE);
    this.source = atlas.source;
    const makeGroup = (blendMode: BLEND_MODES) => {
      const group = new PixiParticleContainer({ texture: atlas, blendMode, dynamicProperties: DYNAMIC_PROPERTIES });
      layer.addChild(group);
      return group;
    };
    const [firstMode, ...moreModes] = blendModes;
    const defaultGroup = makeGroup(firstMode);
    const groups = new Map<BLEND_MODES, PixiParticleContainer>([[firstMode, defaultGroup]]);
    for (const blendMode of moreModes) {
      groups.set(blendMode, makeGroup(blendMode));
    }
    this.groups = groups;
    this.groupList = [...groups.values()];
    this.defaultGroup = defaultGroup;
  }

  /** The live particle count. */
  get count(): number {
    return this.particles.length;
  }

  /** The group for a spawner's blend mode, normal when it passes none. */
  private groupFor(blendMode: BLEND_MODES | undefined): PixiParticleContainer {
    const group = this.groups.get(blendMode ?? 'normal');
    if (group !== undefined) {
      return group;
    }
    if (DEVELOPMENT) {
      console.warn(`Particles: no group for blend mode "${blendMode}", drawing with the first`);
    }
    return this.defaultGroup;
  }

  addParticle(
    type: ParticleTypeId,
    texture: LoadedTextureName,
    speedX: number,
    speedY: number,
    posX: number,
    posY: number,
    scaleX: number,
    scaleY: number,
    alpha: number,
    rotationSpeed: number,
    rotation: number,
    tint?: number,
    blendMode?: BLEND_MODES,
    life?: number,
    scaleFactor?: number,
  ): void {
    if (clock.timeFactorUncapped > SPAWN_SKIP_FACTOR || this.particles.length >= this.maxParticles) {
      return;
    }

    const group = this.groupFor(blendMode);
    const quadTexture = getTexture(texture);
    if (DEVELOPMENT && quadTexture.source !== this.source) {
      console.warn(`Particles: texture "${texture}" is off the sprite atlas and will draw wrong`);
    }
    // The pool is still warming up when it is empty: a new particle and quad
    const particle =
      this.pool.pop() ?? new Particle(new PixiParticle({ texture: quadTexture, anchorX: 0.5, anchorY: 0.5 }), group);
    particle.reset(
      type,
      quadTexture,
      group,
      speedX,
      speedY,
      posX,
      posY,
      scaleX,
      scaleY,
      alpha,
      rotationSpeed,
      rotation,
      tint,
      life,
      scaleFactor,
    );

    // Drawn from this frame on, at the end of its group as the spawn order wants
    group.particleChildren.push(particle.view);
    this.dirty = true;

    const id = this.particles.length;
    if (id > 0) {
      this.particles[this.last]!.next = id;
      particle.prev = this.last;
    } else {
      this.first = id;
    }
    this.last = id;
    this.particles.push(particle);
  }

  /** Spawns a smoke puff here and its shadow in the shadow container; the tint goes on the puff only. */
  addSmokePair(
    shadows: ParticleContainer,
    pair: SmokePair,
    type: ParticleTypeId,
    speedX: number,
    speedY: number,
    posX: number,
    posY: number,
    scaleX: number,
    scaleY: number,
    alpha: number,
    rotationSpeed: number,
    rotation: number,
    tint?: number,
    life?: number,
    scaleFactor?: number,
  ): void {
    this.addParticle(
      type,
      pair[0],
      speedX,
      speedY,
      posX,
      posY,
      scaleX,
      scaleY,
      alpha,
      rotationSpeed,
      rotation,
      tint,
      undefined,
      life,
      scaleFactor,
    );
    shadows.addParticle(
      type,
      pair[1],
      speedX,
      speedY,
      posX,
      posY,
      scaleX,
      scaleY,
      alpha,
      rotationSpeed,
      rotation,
      undefined,
      undefined,
      life,
      scaleFactor,
    );
  }

  /** Returns every particle to the pool, empties the groups and forgets the emitters. */
  wipe(): void {
    this.emitters.clear();
    this.emitterId = 0;
    for (const group of this.groupList) {
      group.particleChildren.length = 0;
      group.update();
    }
    for (const particle of this.particles) {
      this.pool.push(particle);
    }
    this.particles = [];
    this.first = -1;
    this.last = -1;
    this.dirty = false;
  }

  /** Starts a fragment emitter: debris flying from pos at speed, puffing smoke here and shadows there. */
  addEmitter(speed: Vector, pos: Vector, life: number, shadows: ParticleContainer): void {
    if (clock.timeFactorUncapped > SPAWN_SKIP_FACTOR) {
      return;
    }
    this.emitters.set(this.emitterId++, { pos, speed, life, shadows });
  }

  /** Flies every emitter one step and drops a puff behind it; an emitter past its life is removed. */
  private updateEmitters(time: number): void {
    for (const [id, emitter] of this.emitters) {
      emitter.life += 0.02 * time;
      emitter.speed.multiply(1 - 0.02 * time);
      if (emitter.life > 1) {
        this.emitters.delete(id);
        continue;
      }
      emitter.pos.x += emitter.speed.x * time;
      emitter.pos.y += emitter.speed.y * time;

      const pair = toolsRandItem(smokePairs);
      const scale = 0.5 * (1 - emitter.life);
      const lifeRemaining = 1 - emitter.life;
      const rotationSpeed = toolsRand(-0.1, 0.1);
      const rotation = toolsRandCircle();
      // The puff grows towards twice the life the emitter has left, legacy's data argument
      this.addSmokePair(
        emitter.shadows,
        pair,
        particleTypeIdByName.FRAGMENT_SMOKE,
        0,
        0,
        emitter.pos.x,
        emitter.pos.y,
        scale,
        scale,
        1,
        rotationSpeed,
        rotation,
        undefined,
        undefined,
        lifeRemaining + 0.2,
      );
    }
  }

  /** Removes the particle at an index by moving the last one into its slot; returns the index to step to next. */
  private destroy(id: number): number {
    const dying = this.particles[id]!;
    this.pool.push(dying);
    // Its quad is simply not re-listed by the walk; the slots after it shift, so the coordinates go up again
    this.dirty = true;

    const { prev, next } = dying;
    const lastIndex = this.particles.length - 1;
    if (prev !== -1) {
      this.particles[prev]!.next = next;
    } else {
      this.first = next;
    }
    if (next !== -1) {
      this.particles[next]!.prev = prev;
    } else {
      this.last = prev;
    }

    if (id !== lastIndex) {
      const moved = this.particles[lastIndex]!;
      this.particles[id] = moved;
      if (moved.prev !== -1) {
        this.particles[moved.prev]!.next = id;
      } else {
        this.first = id;
      }
      if (moved.next !== -1) {
        this.particles[moved.next]!.prev = id;
      } else {
        this.last = id;
      }
    }
    this.particles.pop();

    return next === lastIndex ? id : next;
  }

  private updateMissile(time: number, particle: Particle): void {
    if (particle.type === particleTypeIdByName.MISSILE) {
      particle.scale.addScalar(0.2 * time);
      particle.scale.clampMax(2 * particle.scaleFactor);
      particle.life += 0.05 * time;
    } else if (particle.type === particleTypeIdByName.MISSILE_FAT) {
      particle.scale.addScalar(0.3 * time);
      particle.scale.clampMax(3 * particle.scaleFactor);
      particle.life += 0.05 * time;
    } else {
      particle.scale.addScalar(0.14 * time);
      particle.scale.clampMax(1.4 * particle.scaleFactor);
      particle.life += 0.08 * time;
    }
    particle.speed.multiply(1 - 0.05 * time);
    particle.alpha = 0.7 * (1 - particle.life);
    particle.colorBgr = rgbToBgr(toolsColorLerp(particle.tint ?? 0xffffff, 0xffffff, 2 * (1 - particle.life)));
  }

  private updateShockwaveSmoke(time: number, particle: Particle): void {
    particle.life += 0.05 * time;
    particle.alpha = 0.7 * toolsEasing.custom(particle.life, 'shockwave');
  }

  private updateShockwave(time: number, particle: Particle): void {
    particle.life += 0.05 * time;
    particle.alpha = 0.4 * toolsEasing.custom(particle.life, 'shockwave');
    particle.scale.both((particle.type === particleTypeIdByName.SHOCKWAVE_OUTER ? 4 : 3) * particle.life);
  }

  private updateExplosionFlash(time: number, particle: Particle): void {
    particle.life += 0.1 * time;
    particle.alpha = 1 - particle.life;
  }

  private updateExplosionFlashBig(time: number, particle: Particle): void {
    particle.life += 0.04 * time;
    particle.alpha = 1 - particle.life;
  }

  private updateExplosionLight(time: number, particle: Particle): void {
    particle.life += 0.09 * time;
    particle.alpha = 1 - particle.life;
  }

  private updateExplosionHotSmoke(time: number, particle: Particle): void {
    particle.life += 0.035 * time;
    particle.alpha = 1 - particle.life;
    particle.scale.addScalar(0.05 * time);
    particle.speed.multiply(1 - 0.1 * time);
    particle.rotationSpeed *= 1 - 0.05 * time;
  }

  private updateExplosionSmoke(time: number, particle: Particle): void {
    particle.life += 0.01 * time;
    particle.alpha = toolsEasing.custom(particle.life, 'explosionSmoke');
    particle.scale.addScalar(0.05 * time);
    particle.speed.multiply(1 - 0.05 * time);
    particle.rotationSpeed *= 1 - 0.05 * time;
  }

  private updateExplosionSpark(time: number, particle: Particle): void {
    particle.life += 0.02 * time;
    particle.alpha = 2 * (1 - particle.life);
    particle.speed.multiply(1 - 0.05 * time);
    particle.rotationSpeed *= 1 - 0.05 * time;
  }

  private updateFragmentSmoke(time: number, particle: Particle): void {
    particle.life += 0.02 * time;
    particle.scale.addScalar(0.075 * time * particle.scaleFactor);
    particle.scale.clampMax(2 * particle.scaleFactor);
    particle.rotationSpeed *= 1 - 0.05 * time;
    particle.alpha = 0.3 * (1 - particle.life);
  }

  private updatePlaneDamage(time: number, particle: Particle): void {
    particle.life += 0.02 * time;
    particle.alpha = 2 * (1 - particle.life);
    particle.speed.multiply(1 - 0.1 * time);
  }

  /**
   * Steps the emitters and every particle by a sub-step of the frame, in spawn order, writes the quads and
   * relists them in their groups, so a death simply drops out of the list.
   */
  update(time: number): void {
    this.updateEmitters(time);

    for (const group of this.groupList) {
      group.particleChildren.length = 0;
    }

    const invShadowScale = this.shadow ? 1 / SHADOW_SCALING : 0;

    for (let index = this.first; index !== -1;) {
      const particle = this.particles[index]!;

      switch (particle.type) {
        case particleTypeIdByName.MISSILE:
        case particleTypeIdByName.MISSILE_FAT:
        case particleTypeIdByName.MISSILE_SMALL:
          this.updateMissile(time, particle);
          break;
        case particleTypeIdByName.SHOCKWAVE_SMOKE:
          this.updateShockwaveSmoke(time, particle);
          break;
        case particleTypeIdByName.SHOCKWAVE_INNER:
        case particleTypeIdByName.SHOCKWAVE_OUTER:
          this.updateShockwave(time, particle);
          break;
        case particleTypeIdByName.EXPLOSION_FLASH:
          this.updateExplosionFlash(time, particle);
          break;
        case particleTypeIdByName.EXPLOSION_FLASH_BIG:
          this.updateExplosionFlashBig(time, particle);
          break;
        case particleTypeIdByName.EXPLOSION_LIGHT:
          this.updateExplosionLight(time, particle);
          break;
        case particleTypeIdByName.EXPLOSION_HOT_SMOKE:
          this.updateExplosionHotSmoke(time, particle);
          break;
        case particleTypeIdByName.EXPLOSION_SMOKE:
          this.updateExplosionSmoke(time, particle);
          break;
        case particleTypeIdByName.EXPLOSION_SPARK:
          this.updateExplosionSpark(time, particle);
          break;
        case particleTypeIdByName.FRAGMENT_SMOKE:
          this.updateFragmentSmoke(time, particle);
          break;
        case particleTypeIdByName.PLANE_DAMAGE:
          this.updatePlaneDamage(time, particle);
          break;
      }

      if (particle.life > 1) {
        index = this.destroy(index);
        continue;
      }

      particle.pos.x += particle.speed.x * time;
      particle.pos.y += particle.speed.y * time;
      particle.rotation += particle.rotationSpeed * time;

      const quad = particle.view;
      let alpha: number;
      if (this.shadow) {
        const shadow = graphicsShadowCoords(particle.pos);
        quad.x = shadow.x;
        quad.y = shadow.y;
        quad.scaleX = particle.scale.x * invShadowScale;
        quad.scaleY = particle.scale.y * invShadowScale;
        alpha = toolsClamp(2 * particle.alpha, 0, 1);
      } else {
        quad.x = particle.pos.x;
        quad.y = particle.pos.y;
        quad.scaleX = particle.scale.x;
        quad.scaleY = particle.scale.y;
        alpha = toolsClamp(particle.alpha, 0, 1);
      }
      // The packed colour the quad wants: alpha byte over the tint, as Pixi's own setters build it
      quad.color = particle.colorBgr + (((alpha * 255) | 0) << 24);

      // The quad's rotation is written only past a visible change, as legacy did
      if (Math.abs(particle.rotation - particle.lastRotation) > 0.03) {
        quad.rotation = particle.rotation;
        particle.lastRotation = particle.rotation;
      }

      particle.group.particleChildren.push(quad);
      index = particle.next;
    }

    if (this.dirty) {
      for (const group of this.groupList) {
        group.update();
      }
      this.dirty = false;
    }
  }
}
